// ecom.ts ↔ game/Ecom.cpp — the in-mission E-Com message overlay: the Ecom()
// entry the EcomAction trigger performs (TriggerActions.cpp:517). The canvas
// EcomForm was removed (canvas-removal R1); the DOM E-Com overlay
// (dom/ingame/ecom-view.ts) renders the message now, fed by the domEcom seam
// with the already-ExpandVars'd text.

import { ExpandVars } from './uikit.ts';
import type { ShellEnv } from './shellform.ts';
import { EnqueueStoryModal } from './simpause.ts';

// game/res.h:37-38, 234, 367-370
export const kidfEcomLarge = 1040;
export const kidfEcomSmall = 1041;
export const kidcMessage = 1001;
export const kidcFrom = 1011;
export const kidcTo = 1012;
export const kidcFromBitmap = 1014;
export const kidcToBitmap = 1015;

// game/res.h:632-635
export const knSmallLargeTypeNone = 0;
export const knSmallLargeTypeSmallBottom = 1;
export const knSmallLargeTypeLarge = 2;
export const knSmallLargeTypeSmallTop = 3;

// game/ht.h:3176 — kctEcomOutputInterval = kctUpdate (8 ticks)
export const kctEcomOutputInterval = 8;

/** The payload the DOM E-Com (Phase 5, Form 6)
 *  overlay renders. `large` (nBackground===knSmallLargeTypeLarge) selects the LARGE
 *  panel (with portraits) vs the SMALL bar; `nBackground` (1=SmallBottom,
 *  2=Large, 3=SmallTop) positions it. The message is the already-resolved text the
 *  canvas would expand (the canvas EcomForm ExpandVars'd it in DoModalEcom; here the
 *  GameSession seam expands it the SAME way before calling domEcom). DISPLAY-only. */
export interface EcomInfo {
  /** the FROM character (s_aszEmailAddrs/s_aszPortraits index). */
  fromIdx: number;
  /** the TO character. */
  toIdx: number;
  /** the (expanded) message text. */
  message: string;
  /** true = the LARGE panel (kidfEcomLarge, with portraits). */
  large: boolean;
  /** the nBackground type (1=SmallBottom, 2=Large, 3=SmallTop). */
  nBackground: number;
  /** the EcomAction fMore flag (display-only — paging is auto, this is just carried). */
  fMore: boolean;
}

/** Render the E-Com message as a DOM overlay. When set,
 *  Ecom() branches to this INSIDE the EnqueueStoryModal show callback (so it stays
 *  queued + SP-paused like the canvas form did); `done()` is the C++ post-modal return point
 *  the host calls when the overlay is dismissed (it unmounts + releases the queue's
 *  next()). Absent (a headless harness) ⇒ no UI shown. */
export type EcomDomHook = (info: EcomInfo, done: () => void) => void;

/** The C++ globals Ecom()/EcomForm reach: gpmfrmm/gpiniForms/gtimm/gsndm +
 *  ggame.GetPlayfieldSize/gpdisp->GetSafeAreas + gsim.Pause/gfMultiplayer (the
 *  EcomAction pause arm) — the ShellEnv bundle carries them all (the
 *  GameSession's shellEnv in-game). Plus the E-Com overlay seam (domEcom) +
 *  the form-host getVar (so the DOM path expands the message {pvar}-identically). */
export type EcomEnv = Pick<
  ShellEnv,
  'mfrmm' | 'gtimm' | 'sndm' | 'GetPlayfieldSize' | 'getSafeAreas' | 'sim' | 'fMultiplayer'
> & {
  /** the E-Com overlay seam (Phase 5, Form 6). */
  domEcom?: EcomDomHook;
};

// game/Ecom.cpp:64-123 — Ecom (the EcomAction::Perform UI arm). done = the
// C++ post-DoModal `delete pfrm` return point.
//
// The C++ call BLOCKS with gsim.Pause held around it (EcomAction::Perform,
// TriggerActions.cpp:519-525; MP returned before reaching it) — so several
// ecoms raised by one trigger pass show ONE AT A TIME, in Perform order.
// EnqueueStoryModal (simpause.ts) carries both the sequencing and the pause.
export function Ecom(
  env: EcomEnv,
  nCharFrom: number,
  nCharTo: number,
  pszMessage: string,
  nBackground: number,
  fMore: boolean,
  done: (() => void) | null = null,
): void {
  void fMore;
  // (the #ifdef STRESS recording arm is compiled out of the ship build)

  EnqueueStoryModal(env, true, (next) => {
    // render the E-Com as a DOM overlay over the live canvas — INSIDE the show
    // callback, so it stays QUEUED + SP-PAUSED exactly like the canvas form did (the
    // EnqueueStoryModal wrapping is unchanged). The message is ExpandVars'd here the
    // SAME way DoModalEcom did, so the {pvar}/{$ranktitle} text matches byte-for-byte.
    // DISPLAY-only — no sim/command/wire change.
    if (env.domEcom !== undefined) {
      const message = ExpandVars(pszMessage, (35 * 1024) / 2, env.mfrmm.host.getVar);
      env.domEcom(
        {
          fromIdx: nCharFrom,
          toIdx: nCharTo,
          message,
          large: nBackground === knSmallLargeTypeLarge,
          nBackground,
          fMore,
        },
        () => {
          if (done !== null) done();
          next(); // release the pause hold + show the next queued story modal
        },
      );
      return;
    }

    // No DOM E-Com seam wired (e.g. a headless harness) — release the queue.
    if (done !== null) done();
    next();
  });
}
